import { useIsSmallScreen } from '../../hooks/useResponsive';

const HorizontalTableNavigation = ({
  showHorizontalPager,
  currentHorizontalPage,
  horizontalPages,
  scrollToHorizontalPage,
  handleArrowClick,
  canScrollRight = true,
  position = "below", // "above" or "below"
  variant = "dots" // "dots", "arrow", or "both"
}) => {
  const isSmallScreen = useIsSmallScreen();

  if (!showHorizontalPager) return null;

  const renderDots = () => {
    const pagesPerDot = isSmallScreen ? 2 : 1;

    // Calculate total pages available (original logic: horizontalPages - 1)
    const totalPages = Math.max(1, horizontalPages - 1);

    // Calculate number of dots based on screen size
    const totalDots = Math.max(1, Math.ceil(totalPages / pagesPerDot));

    // Calculate which dot should be active based on current page
    const getCurrentActiveDot = () => {
      const effectiveCurrentPage = Math.max(0, Math.min(currentHorizontalPage, totalPages - 1));
      return Math.floor(effectiveCurrentPage / pagesPerDot);
    };

    const activeDotIndex = getCurrentActiveDot();

    return (
      <div className="flex items-center space-x-2" data-intro={position === "above" ? "horizontal-dots-pager-above" : "horizontal-dots-pager"}>
        {Array.from({ length: totalDots }, (_, index) => {
          // Calculate the target page when this dot is clicked
          const targetPage = index * pagesPerDot;

          // For title, show the range this dot represents
          const startPage = targetPage + 1;
          const endPage = Math.min(targetPage + pagesPerDot, totalPages);
          const titleText = pagesPerDot === 1
            ? `View columns ${startPage}`
            : `View columns ${startPage}-${endPage}`;

          return (
            <button
              key={index}
              onClick={() => scrollToHorizontalPage(targetPage)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === activeDotIndex
                  ? 'bg-blue-600 dark:bg-blue-400 scale-125'
                  : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              title={titleText}
            />
          );
        })}
      </div>
    );
  };

  const renderArrow = () => (
    <div data-intro={position === "above" ? "horizontal-arrow-above" : "horizontal-arrow-below"}>
      <button
        onClick={handleArrowClick}
        disabled={!canScrollRight}
        className={`rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-all duration-200 ${
          !canScrollRight
            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-50'
            : 'bg-blue-600 hover:bg-blue-700 text-white opacity-80 hover:opacity-100'
        }`}
        title={!canScrollRight ? "No more pages" : "Next page"}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  // For above position with both dots and arrow
  if (position === "above" && variant === "both") {
    return (
      <div className="bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 border-b-0 sm:rounded-t-lg px-2 py-2">
        <div className="flex justify-between items-center">
          <div></div> {/* Left spacer */}
          {renderDots()}
          {renderArrow()}
        </div>
      </div>
    );
  }

  // For below position with both dots and arrow
  if (position === "below" && variant === "both") {
    return (
      <div className="flex justify-between items-center pl-6 pr-2 py-2 border-b border-gray-200 dark:border-gray-700">
        <div></div> {/* Left spacer */}
        {renderDots()}
        {renderArrow()}
      </div>
    );
  }

  // For dots only (original behavior)
  if (variant === "dots") {
    return (
      <div className="flex justify-center mt-3 mb-2">
        <div className="flex items-center space-x-2 p-3 rounded">
          {renderDots()}
        </div>
      </div>
    );
  }

  // For arrow only
  if (variant === "arrow") {
    return (
      <div className="flex justify-end">
        {renderArrow()}
      </div>
    );
  }

  return null;
};

export default HorizontalTableNavigation;
